import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { API_URL } from '../../constant';
import './PremiumCardCollection.css';
import wallet2 from "../../images/shapes/Wallet03.1 1.png";
import oldbank from "../../images/shapes/bank-pound-icon2 4.png";

// SVG Icons as inline components
const WalletIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
        <line x1="1" y1="10" x2="23" y2="10" />
    </svg>
);

const HistoryIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14,2 14,8 20,8" />
        <line x1="16" y1="13" x2="8" y2="13" />
        <line x1="16" y1="17" x2="8" y2="17" />
        <polyline points="10,9 9,9 8,9" />
    </svg>
);

const BankIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 21h18" />
        <path d="M3 10h18" />
        <path d="M5 6l7-3 7 3" />
        <path d="M4 10v11" />
        <path d="M20 10v11" />
        <path d="M8 14v3" />
        <path d="M12 14v3" />
        <path d="M16 14v3" />
    </svg>
);

const CardIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="3" width="20" height="18" rx="3" ry="3" />
        <circle cx="12" cy="12" r="4" />
        <path d="M2 8h20" />
    </svg>
);

function PremiumCardCollection() {
    const outletContext = useOutletContext() || {};
    const { userData } = outletContext;

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // Card Promotion Wallets state — primary data source
    const [promoWallets, setPromoWallets] = useState(null);
    const [topcoinWorth, setTopcoinWorth] = useState(0.005);

    // History modal state
    const [showHistoryModal, setShowHistoryModal] = useState(false);
    const [historyType, setHistoryType] = useState(null);
    const [historyData, setHistoryData] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(false);

    const getAuthHeaders = useCallback(() => {
        const token = localStorage.getItem('auth_token');
        return {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json',
        };
    }, []);

    // The 3 card promotion types in display order
    const cardTypes = [
        { key: 'reward_card', name: 'Reward Card Promotion', walletType: 'reward_card' },
        { key: 'extra_card', name: 'Extra Card Promotion', walletType: 'extra_card' },
        { key: 'bonus_card', name: 'Bonus Card Promotion', walletType: 'bonus_card' },
    ];

    // Fetch card promotion wallet balances from card_promotion_wallets table
    const fetchPromoWallets = useCallback(async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('auth_token');
            if (!token) {
                setError('Not authenticated');
                return;
            }

            const response = await fetch(`${API_URL}/api/customer/collectable-cards/promotion-wallets`, {
                headers: getAuthHeaders(),
            });

            if (response.ok) {
                const data = await response.json();
                if (data.success) {
                    setPromoWallets(data.wallets);
                    setTopcoinWorth(data.topcoin_worth || 0.005);
                } else {
                    setPromoWallets(null);
                }
            } else if (response.status === 401) {
                setError('Session expired. Please log in again.');
            } else {
                setPromoWallets(null);
            }
        } catch (err) {
            console.warn('Promotion wallets API not available:', err.message);
            setPromoWallets(null);
        } finally {
            setLoading(false);
        }
    }, [getAuthHeaders]);

    useEffect(() => {
        fetchPromoWallets();
    }, [fetchPromoWallets]);

    // Get balance for a given card key from the API response
    const getBalance = (cardKey) => {
        if (!promoWallets || !promoWallets[cardKey]) {
            return { topcoin: 0, gbp: '0.00' };
        }
        const wallet = promoWallets[cardKey];
        return {
            topcoin: wallet.balance_topcoin || 0,
            gbp: (wallet.balance_gbp || 0).toFixed(2),
        };
    };

    // Fetch promotion wallet transaction history
    const handleShowHistory = async (walletType, walletName) => {
        setHistoryType({ name: walletName, wallet_type: walletType });
        setShowHistoryModal(true);
        setHistoryLoading(true);
        setHistoryData([]);

        try {
            const response = await fetch(
                `${API_URL}/api/customer/collectable-cards/promotion-wallets?wallet_type=${walletType}`,
                { headers: getAuthHeaders() }
            );

            if (response.ok) {
                const data = await response.json();
                if (data.success) {
                    setHistoryData(data.transactions?.data || data.transactions || []);
                }
            }
        } catch (err) {
            console.warn('History fetch failed:', err.message);
        } finally {
            setHistoryLoading(false);
        }
    };

    // Format date
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            return new Date(dateString).toLocaleDateString('en-GB', {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
            });
        } catch {
            return dateString;
        }
    };

    // Loading state
    if (loading) {
        return (
            <div className="premium-cards-wrapper">
                <div className="premium-loading">
                    <div className="premium-spinner"></div>
                </div>
            </div>
        );
    }

    return (
        <div className="premium-cards-wrapper w">
            {/* Header */}
            <div className="premium-cards-header">
                <div className="premium-cards-header-left">
                    <span className="premium-cards-label">Premium</span>
                    <h2 className="premium-cards-title">Collectable Cards</h2>
                </div>
                <div className="premium-cards-header-right">
                    <CardIcon />
                </div>
            </div>

            {/* Promotion Cards List */}
            <div className="promotion-cards-list">
                {cardTypes.map((card) => {
                    const balance = getBalance(card.key);
                    return (
                        <div key={card.key} className="promotion-card-row">
                            <div className="promotion-card-top">
                                <div className="promotion-icon-circle">
                                    <img src={wallet2} alt="Wallet" className="wallet-image" style={{ backgroundColor: "white" }} />
                                </div>
                                <h3 className="promotion-card-title">{card.name}</h3>
                            </div>
                            <div className="promotion-card-bottom">
                                <span className="promotion-count-badge">
                                    {balance.topcoin > 0
                                        ? `${balance.topcoin.toLocaleString()} TC (£${balance.gbp})`
                                        : '0 TC (£0.00)'
                                    }
                                </span>
                                <button
                                    className="promotion-history-btn"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleShowHistory(card.walletType, card.name);
                                    }}
                                >
                                    <HistoryIcon />
                                    History
                                </button>
                            </div>
                        </div>
                    );
                })}

                {/* Withdraw Row */}
                <div className="withdraw-card-row">
                    <div className="withdraw-card-top">
                        <div className="withdraw-icon-circle">
                            <img src={oldbank} alt="Bank" className="bank-image" style={{ backgroundColor: "white" }} />
                        </div>
                        <h3 className="withdraw-card-title">Withdraw</h3>
                    </div>
                    <div className="withdraw-card-bottom">
                        <span className="withdraw-amount-badge"></span>
                        <button className="withdraw-btn" disabled>
                            Withdraw
                        </button>
                    </div>
                </div>
            </div>

            {/* History Modal */}
            {showHistoryModal && historyType && (
                <div className="history-modal-overlay" onClick={() => setShowHistoryModal(false)}>
                    <div className="history-modal-box" onClick={(e) => e.stopPropagation()}>
                        <button
                            className="history-modal-close"
                            onClick={() => setShowHistoryModal(false)}
                        >
                            &times;
                        </button>

                        <h3 className="history-modal-title">
                            {historyType.name} — Transaction History
                        </h3>

                        {historyLoading ? (
                            <div className="premium-loading">
                                <div className="premium-spinner"></div>
                                <p>Loading history...</p>
                            </div>
                        ) : historyData.length > 0 ? (
                            <div className="history-list">
                                {historyData.map((tx, index) => (
                                    <div key={tx.id || index} className="history-item">
                                        <div className="history-item-left">
                                            <span className="history-item-type">
                                                {tx.source_type === 'shop_transaction' ? '🛒 Shop Reward' :
                                                 tx.source_type === 'card_purchase' ? '🎁 Purchase Reward' :
                                                 tx.transaction_type === 'purchase' ? '🛒 Purchase' :
                                                 tx.transaction_type === 'redemption' ? '🎁 Redemption' :
                                                 '↩️ Credit'}
                                            </span>
                                            <span className="history-item-date">
                                                {formatDate(tx.created_at)}
                                            </span>
                                            <span className="history-item-ref">
                                                {tx.source_ref || tx.reference_code || ''}
                                            </span>
                                        </div>
                                        <div className="history-item-right">
                                            <div className="history-item-amount">
                                                +{(tx.amount || tx.topcoin_amount || 0).toLocaleString()} TC
                                            </div>
                                            <div className="history-item-balance">
                                                Balance: {(tx.balance_after || tx.wallet_balance_after || 0).toLocaleString()} TC
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="history-empty">
                                <div className="history-empty-icon">📋</div>
                                <p className="history-empty-text">No transactions yet for this card type.</p>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

export default PremiumCardCollection;